/**
 * KalenderSettings — Verwaltung der Kalender-Kategorien.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, SectionTitle, Button, Field, Input } from '../../components/ui';
import { getThemeColors } from '../../lib/theme';
import { v2CalendarCategories, type CalendarCategory } from '../../lib/calendarEvents';
import { msgraphApi } from '../../lib/msgraph';

interface KalenderSettingsProps {
  dark: boolean;
}

interface EditState {
  name: string;
  color: string;
}

const DEFAULT_COLOR = '#6b7280';

export function KalenderSettings({ dark }: KalenderSettingsProps) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();

  const [categories, setCategories] = useState<CalendarCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Inline edit state
  const [editId, setEditId] = useState<number | null>(null);
  const [editState, setEditState] = useState<EditState>({ name: '', color: DEFAULT_COLOR });
  const [editError, setEditError] = useState<string | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  // Add-form state
  const [addName, setAddName] = useState('');
  const [addColor, setAddColor] = useState(DEFAULT_COLOR);
  const [addError, setAddError] = useState<string | null>(null);
  const [addSaving, setAddSaving] = useState(false);

  // Per-row delete
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [deleteErrors, setDeleteErrors] = useState<Record<number, string>>({});

  const loadCategories = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await v2CalendarCategories.list();
      setCategories(data);
    } catch {
      setLoadError(tr('settings.calendar.load_error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadCategories();
  }, []);

  // Outlook-Spiegelung: wer vor dem Kategorie-Ausbau verbunden hat, bekommt
  // die Kategorien nur ohne Farbe → Hinweis zum Neu-Verbinden. Der Status ist
  // reine Zusatzinfo; schlägt er fehl, bleibt nur der allgemeine Hinweis.
  const [needsReconnect, setNeedsReconnect] = useState(false);
  useEffect(() => {
    let alive = true;
    msgraphApi.status()
      .then(s => {
        if (alive) setNeedsReconnect(s.connected && s.categories_enabled === false);
      })
      .catch(() => { /* ohne Status kein Reconnect-Hinweis */ });
    return () => { alive = false; };
  }, []);

  const openEdit = (cat: CalendarCategory) => {
    setEditId(cat.id);
    setEditState({ name: cat.name, color: cat.color });
    setEditError(null);
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditError(null);
  };

  const saveEdit = async () => {
    if (!editId) return;
    setEditSaving(true);
    setEditError(null);
    try {
      const updated = await v2CalendarCategories.update(editId, {
        name: editState.name,
        color: editState.color,
      });
      setCategories(prev => prev.map(c => (c.id === editId ? updated : c)));
      setEditId(null);
    } catch {
      setEditError(tr('settings.calendar.error.save'));
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    setDeleteErrors(prev => ({ ...prev, [id]: '' }));
    try {
      await v2CalendarCategories.remove(id);
      setCategories(prev => prev.filter(c => c.id !== id));
    } catch {
      setDeleteErrors(prev => ({ ...prev, [id]: tr('settings.calendar.error.delete') }));
    } finally {
      setDeletingId(null);
    }
  };

  const handleAdd = async () => {
    if (!addName.trim()) {
      setAddError(tr('settings.calendar.error.name_required'));
      return;
    }
    setAddSaving(true);
    setAddError(null);
    try {
      const created = await v2CalendarCategories.create({ name: addName.trim(), color: addColor });
      setCategories(prev => [...prev, created]);
      setAddName('');
      setAddColor(DEFAULT_COLOR);
    } catch {
      setAddError(tr('settings.calendar.error.create'));
    } finally {
      setAddSaving(false);
    }
  };

  const ROW_STYLE: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 0',
    borderBottom: `1px solid ${t.borderSoft}`,
    flexWrap: 'wrap',
  };

  const INPUT_STYLE: React.CSSProperties = {
    border: `1px solid ${t.border}`, borderRadius: 8,
    padding: '7px 10px', fontSize: 13, color: t.heading,
    background: t.inputBg, fontFamily: 'Outfit, sans-serif',
    outline: 'none', width: '100%',
  };

  if (loading) {
    return (
      <Card dark={dark} style={{ padding: 20 }}>
        <div style={{ color: t.muted, fontSize: 13 }}>{tr('settings.calendar.loading')}</div>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card dark={dark} style={{ padding: 20 }}>
        <div style={{ color: '#b42318', fontSize: 13, marginBottom: 12 }}>{loadError}</div>
        <Button variant="secondary" size="sm" dark={dark} onClick={() => void loadCategories()}>
          {tr('settings.calendar.retry')}
        </Button>
      </Card>
    );
  }

  return (
    <Card dark={dark} style={{ padding: 20 }}>
      <SectionTitle
        dark={dark}
        title={tr('settings.calendar.title')}
        subtitle={tr('settings.calendar.subtitle')}
      />

      <div style={{ fontSize: 12, color: t.muted, lineHeight: 1.5, marginBottom: 10 }}>
        {tr('settings.calendar.outlook_hint')}
      </div>
      {needsReconnect && (
        <div
          role="note"
          style={{
            fontSize: 12, lineHeight: 1.5, color: t.heading,
            background: t.cardMuted, border: `1px solid ${t.borderSoft}`,
            borderRadius: 8, padding: '8px 10px', marginBottom: 10,
          }}
        >
          {tr('settings.calendar.outlook_reconnect')}
        </div>
      )}

      {categories.map(cat => (
        <div key={cat.id}>
          {/* Row */}
          <div style={ROW_STYLE}>
            {/* Color dot */}
            <div style={{
              width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
              background: cat.color || DEFAULT_COLOR,
              border: `1px solid ${t.border}`,
            }} />

            {/* Name */}
            <div style={{ flex: 1, minWidth: 100 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: t.heading }}>{cat.name}</div>
            </div>

            {/* Edit */}
            <Button variant="secondary" size="sm" dark={dark} onClick={() => openEdit(cat)}>
              {tr('common.action.edit')}
            </Button>

            {/* Delete */}
            <Button
              variant="secondary"
              size="sm"
              dark={dark}
              disabled={deletingId === cat.id}
              onClick={() => void handleDelete(cat.id)}
            >
              {deletingId === cat.id ? '…' : tr('common.action.delete')}
            </Button>
          </div>

          {/* Delete error */}
          {deleteErrors[cat.id] && (
            <div style={{ fontSize: 12, color: '#b42318', marginBottom: 6 }}>
              {deleteErrors[cat.id]}
            </div>
          )}

          {/* Inline edit form */}
          {editId === cat.id && (
            <div style={{
              padding: 14, background: t.cardMuted, border: `1px solid ${t.borderSoft}`,
              borderRadius: 10, marginBottom: 8,
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Field label={tr('settings.calendar.field.name')}>
                  <Input
                    value={editState.name}
                    onChange={e => setEditState(s => ({ ...s, name: e.target.value }))}
                    dark={dark}
                  />
                </Field>
                <Field label={tr('settings.calendar.field.color')}>
                  <input
                    type="color"
                    value={editState.color}
                    onChange={e => setEditState(s => ({ ...s, color: e.target.value }))}
                    style={{ width: '100%', height: 38, border: `1px solid ${t.border}`, borderRadius: 8, cursor: 'pointer', background: 'none' }}
                  />
                </Field>
              </div>

              {editError && <div style={{ fontSize: 12, color: '#b42318' }}>{editError}</div>}

              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="primary" size="sm" dark={dark} disabled={editSaving} onClick={() => void saveEdit()}>
                  {editSaving ? tr('settings.calendar.saving') : tr('common.action.save')}
                </Button>
                <Button variant="secondary" size="sm" dark={dark} onClick={cancelEdit}>
                  {tr('common.action.cancel')}
                </Button>
              </div>
            </div>
          )}
        </div>
      ))}

      {/* Add form */}
      <div style={{
        padding: 14, background: t.cardMuted, border: `1px solid ${t.borderSoft}`,
        borderRadius: 10, marginTop: 14,
        display: 'flex', flexDirection: 'column', gap: 10,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: t.heading, marginBottom: 4 }}>
          {tr('settings.calendar.new_heading')}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'end' }}>
          <Field label={tr('settings.calendar.field.name')}>
            <input
              value={addName}
              onChange={e => setAddName(e.target.value)}
              style={INPUT_STYLE}
              placeholder={tr('settings.calendar.field.name_placeholder')}
            />
          </Field>
          <Field label={tr('settings.calendar.field.color')}>
            <input
              type="color"
              value={addColor}
              onChange={e => setAddColor(e.target.value)}
              style={{ width: 60, height: 38, border: `1px solid ${t.border}`, borderRadius: 8, cursor: 'pointer', background: 'none' }}
            />
          </Field>
        </div>

        {addError && <div style={{ fontSize: 12, color: '#b42318' }}>{addError}</div>}

        <div>
          <Button variant="primary" size="sm" dark={dark} disabled={addSaving} onClick={() => void handleAdd()}>
            {addSaving ? tr('settings.calendar.adding') : tr('common.action.add')}
          </Button>
        </div>
      </div>
    </Card>
  );
}
